import { ref, computed, watch } from 'vue'
import _ from 'lodash'
import log from 'loglevel'
import store from '@/store/index.js'
import { LOCAL_STORAGE_VISIBLE_KEY } from '@/constants.js'

/**
 * 対象レイヤーの visible を localStorage に永続化する
 * AppMapLayerController で使用される想定
 */
export function useStoreLayerVisible(props) {
  /**
   * 保存対象レイヤーID
   * * null 時は watcher による保存処理は行われない
   * @type {Array.<String>}
   */
  const targetLayerIds = ref(null)

  /**
   * visible(監視用)
   * @type {Object}
   */
  const visible = computed(() => {
    if (!targetLayerIds.value) return null

    const result = {}
    for (const [key, value] of Object.entries(
      store.getters['map/layerState'].visible
    )) {
      if (targetLayerIds.value.includes(key)) {
        result[key] = value
      }
    }
    return result
  })

  /**
   * localStorageからvisible状態を復元する
   */
  const restoreLayerVisibleFromLocalStorage = () => {
    log.debug('[DEBUG] try restore visible from localStorage')
    const visibles = _getVisibleFromLocalStorage(store.state.login.userId)
    if (visibles) {
      log.debug('[DEBUG] visible found in localStorage', visibles)
      store.dispatch(
        'map/setVisible',
        Object.entries(visibles).map(([id, visible]) => ({ id, visible }))
      )
    }
  }

  /**
   * localStorageからvisibleを取得する
   * @param {Number} userId 対象ユーザーID(未指定時は全取得)
   */
  const _getVisibleFromLocalStorage = (userId) => {
    let value = localStorage.getItem(LOCAL_STORAGE_VISIBLE_KEY)
    try {
      value = JSON.parse(value)
    } catch (e) {
      log.debug('[DEBUG] failed to parse', value, e)
      return null
    }

    if (userId) {
      return value && value[`${userId}`]
    } else {
      return value
    }
  }

  /**
   * localStorageにvisible状態を保存する
   */
  const saveLayerVisibleToLocalStorage = () => {
    // Vueのリアクティブ監視プロパティを排除する
    const value = JSON.parse(JSON.stringify(visible.value))

    log.debug('[DEBUG] save visible to localStorage', value)
    localStorage.setItem(
      LOCAL_STORAGE_VISIBLE_KEY,
      JSON.stringify(_mergeVisibleToStoraged(store.state.login.userId, value))
    )
  }

  /**
   * localStorageの値に保存したいvisible値をマージする
   * @param {Number} userId ユーザーID
   * @param {Object} visible visible値
   * @returns {Object} マージされたデータ
   */
  const _mergeVisibleToStoraged = (userId, visible) => {
    const value = _getVisibleFromLocalStorage()
    if (value) {
      return { ...value, [userId]: visible }
    } else {
      return { [userId]: visible }
    }
  }

  // visibleに変化があったらlocalStorageに保存
  // AppMapLayerController がローカルモードの時は何もしない
  if (props.useGlobalRenderState) {
    watch(
      visible,
      _.debounce(
        (value) =>
          store.state.mapLayer.enableVisibleAutoSave &&
          value &&
          saveLayerVisibleToLocalStorage(),
        0
      ),
      { deep: true }
    )
  }

  return {
    targetLayerIds,
    restoreLayerVisibleFromLocalStorage,
    saveLayerVisibleToLocalStorage,
  }
}
